import './VerifyingSustainability.css';
import { Helmet } from 'react-helmet';


import React from 'react';

const VerifyingSustainability = () => {
  return (
    <div className="sustainability-page">

<Helmet>
  <title>Our Sustainability Promise | Verdure</title>
  <meta name="description" content="Learn how Verdure ensures every brand on our platform meets rigorous sustainability and ethical standards." />
  <meta property="og:title" content="Our Sustainability Promise | Verdure" />
  <meta property="og:description" content="Transparency. Ethics. Real impact. Here’s how we vet and support every indie brand on Verdure." />
</Helmet>
      <div className="sustainability-hero">
        <div className="container">
          <h1>Verdure Sustainability Criteria</h1>
          <p className="lead">
            Our commitment to verified sustainability practices ensures every product on our platform meets rigorous ethical and environmental standards.
          </p>
        </div>
      </div>

      <div className="container">
        <div className="sustainability-intro">
          <p>
            Verdure has guidelines on what can and cannot be listed on our marketplace to ensure sustainability is verified. 
            Our process follows five main principles that help us create a marketplace where ethical, accessible, and 
            future-forward shopping is possible—for everyone.
          </p>
        </div>

        <div className="criteria-section">
          <div className="criteria-item">
            <div className="criteria-header">
              <div className="criteria-number">1</div>
              <h2>Production Materials</h2>
            </div>
            <div className="criteria-content">
              <p>
                To be listed on Verdure, products must be made with at least 80% sustainable materials. For 
                clothing, this includes organic cotton, hemp, bamboo, recycled fibers, and sustainable synthetics like TENCEL™. 
                We also welcome the use of upcycled or repurposed materials from previous product cycles.
              </p>
              <p>
                We understand that even sustainable options (such as cotton) can carry environmental trade-offs like water 
                consumption, which is why we assess sustainability holistically. Businesses are provided the opportunity to 
                disclose the materials used in their products during onboarding.
              </p>
              <p>
                Other eco-conscious material choices include soy-based inks, biodegradable components, and recyclable packaging elements.
              </p>
            </div>
          </div>

          <div className="criteria-item">
            <div className="criteria-header">
              <div className="criteria-number">2</div>
              <h2>Labor for Production</h2>
            </div>
            <div className="criteria-content">
              <p>
                All businesses on Verdure must comply with ethical labor practices. If a brand works with external factories, 
                they must ensure workers are paid a living wage and operate in safe, healthy working conditions.
              </p>
              <p>Verdure does not allow businesses that:</p>
              <ul className="criteria-list">
                <li>Engage in forced labor or child labor</li>
                <li>Violate labor safety standards</li>
                <li>Fail to meet basic worker protection laws</li>
              </ul>
              <p>
                We reference global standards on Decent Work as a baseline for evaluating fair labor.
              </p>
            </div>
          </div>

          <div className="criteria-item">
            <div className="criteria-header">
              <div className="criteria-number">3</div>
              <h2>Shipping, Packaging and Processes</h2>
            </div>
            <div className="criteria-content">
              <p>
                Shipping and packaging can be challenging areas, especially for smaller or growing businesses. 
                That's why Verdure, alongside our nonprofit and student-led partners, evaluates a brand's shipping 
                practices on a case-by-case basis.
              </p>
              <p>We encourage companies to:</p>
              <ul className="criteria-list">
                <li>Use minimal or recycled packaging</li>
                <li>Opt for biodegradable or compostable materials</li>
                <li>Choose sustainable shipping carriers when possible</li>
              </ul>
              <p>
                While carbon offset programs are not required, we support and highlight brands exploring these initiatives. 
                It's important to note that offsetting emissions does not equate to a fully sustainable product, but it can 
                be a meaningful step toward climate responsibility.
              </p>
            </div>
          </div>

          <div className="criteria-item">
            <div className="criteria-header">
              <div className="criteria-number">4</div>
              <h2>No Animal Cruelty and Ecological Impact</h2>
            </div>
            <div className="criteria-content">
              <p>
                Verdure does not support businesses involved in animal testing or animal cruelty. Products must be 
                vegan-friendly and free from animal-derived materials, unless disclosed otherwise. A petition can be made for using beeswax, however, the business must provide information of where it sources it from, 
                along with clear ways it provides back to helping bees.
              </p>
              <p>
                We also evaluate a business's ecological footprint, particularly in how product sourcing affects natural ecosystems. 
                This includes:
              </p>
              <ul className="criteria-list">
                <li>Avoiding harm to animal habitats</li>
                <li>Preventing deforestation</li>
                <li>Taking action against water and soil pollution in the supply chain</li>
              </ul>
              <p>
                We prioritize brands that take a stand on environmental issues and actively reduce their ecological impact.
              </p>
            </div>
          </div>

          <div className="criteria-item">
            <div className="criteria-header">
              <div className="criteria-number">5</div>
              <h2>Transparency and Overall Impact</h2>
            </div>
            <div className="criteria-content">
              <p>
                Transparency is at the heart of Verdure. All brands are required to provide accurate, detailed 
                sustainability information during onboarding, which we cross-reference through independent research 
                and public records.
              </p>
              <p>We also consider a brand's overall impact, which may include:</p>
              <ul className="criteria-list">
                <li>Circular economy practices (e.g., product take-back or repair programs)</li>
                <li>Biodegradable or refillable product design</li>
                <li>Support for local communities or charitable initiatives</li>
                <li>Low-carbon or carbon-neutral operations</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="sustainability-resources">
          <h2>Additional Resources</h2>
          <p>
            Please see additional places to learn more about good business standards. 
            These have informed us on how to create this criteria specific for Verdure:
          </p>
          <ul className="resources-list">
            <li><a href="https://www.bcorporation.net/" target="_blank" rel="noopener noreferrer">B Lab</a></li>
            <li><a href="#" target="_blank" rel="noopener noreferrer">The Conscious Buyer</a></li>
            <li><a href="#" target="_blank" rel="noopener noreferrer">World Youth</a></li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default VerifyingSustainability;